import { PlusIcon } from '@heroicons/react/solid';
import { t } from 'i18next';
import { MouseEventHandler, useCallback } from 'react';

import { BadgeText, Button, IconSvg, Text } from '@/lib/v2/components';
import { EditPencilIcon } from '@/lib/v2/icons/outline';
import { ModalSingleInput } from '@/lib/v2/organisms';
import {
  IOnSubmitModalSingleInput,
  IValidationResponse,
} from '@/lib/v2/organisms/ModalSingleInput/ModalSingleInput';

import { TextFiltersApplied } from '@/src/modules/AutomationModuleNew/components/TextFiltersApplied';
import { useRulesValidations } from '@/src/modules/AutomationModuleNew/hooks/useRulesValidations';

import { EStates } from '@/modules/CampaignsModule/interfaces/Campaigns';

type HeaderTableProps = {
  title: string;
  titleModal?: string;
  total: number;
  partial?: number;
  buttonText?: string;
  onClickButton?: MouseEventHandler<HTMLButtonElement>;
  isOpenModal?: boolean;
  onCloseModal?: () => void;
  hasFiltersApplied?: boolean;
  textFiltersModule?: string;
  onValidateCreateFetch?: (value: string) => Promise<IValidationResponse>;
  onSubmitCreateFetch?: IOnSubmitModalSingleInput;
  isLoading?: boolean;
  state?: EStates;
  icon?: JSX.Element;
  isEditButtonIcon?: boolean;
  isDisabledEditButton?: boolean;
};

const HeaderTable = ({
  title,
  titleModal,
  total,
  buttonText,
  onClickButton,
  isOpenModal = false,
  onCloseModal,
  partial,
  hasFiltersApplied,
  textFiltersModule,
  onValidateCreateFetch,
  onSubmitCreateFetch,
  isLoading = false,
  state,
  icon,
  isEditButtonIcon,
  isDisabledEditButton,
}: HeaderTableProps) => {
  const { VALIDATION_RULES } = useRulesValidations();

  const handleClose = useCallback(() => {
    onCloseModal?.();
  }, [onCloseModal]);

  return (
    <>
      <div className="flex items-center justify-between border-b-2 border-b-emblue-app-background px-6 pb-4">
        <div className="flex items-center gap-2">
          {icon && (
            <IconSvg
              rounded
              bgColor="headingIcon"
              height="auto"
              svgComponent={icon}
              width="44px"
              withPadding="medium"
            />
          )}
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <Text as="span" fontWeight="medium" variant="sub-headline">
                {title}{' '}
                <Text as="span" color="primary" fontWeight="medium" variant="sub-headline">
                  ({total})
                </Text>
              </Text>
              {state !== undefined && (
                <BadgeText
                  text={
                    state === EStates.ACTIVE
                      ? (t('CAMPAIGNS_MAIN.STATES.active') as unknown as string)
                      : (t('CAMPAIGNS_MAIN.STATES.inactive') as unknown as string)
                  }
                  variantColor={state === EStates.ACTIVE ? 'success' : 'inactive'}
                />
              )}
            </div>
            <TextFiltersApplied
              hasFiltersApplied={hasFiltersApplied ?? false}
              moduleText={textFiltersModule ?? ''}
              partial={partial ?? 0}
            />
          </div>
        </div>
        {buttonText && (
          <Button
            disabled={isDisabledEditButton}
            iconLeft={
              isEditButtonIcon ? (
                <IconSvg strokeColor="white" svgComponent={<EditPencilIcon />} />
              ) : (
                <PlusIcon className="size-4" />
              )
            }
            size="small"
            onClick={onClickButton}
          >
            {buttonText}
          </Button>
        )}
      </div>
      <ModalSingleInput
        isLoading={isLoading}
        isOpen={isOpenModal}
        rules={VALIDATION_RULES}
        title={titleModal ?? ''}
        onClose={handleClose}
        onSubmit={onSubmitCreateFetch}
        onValidateInputFetch={onValidateCreateFetch}
      />
    </>
  );
};

export default HeaderTable;
